<div class="card">
  <h2>API Documentation</h2>

  <h3>Sensor Data Ingestion</h3>
  <p>To send sensor data to the system, make a POST request to the following endpoint:</p>

  <div class="code-block">
    <code>POST https://integrationflex-apim.azure-api.net/opsviewer/IngestSensor?apiKey=YOUR_API_KEY</code>
  </div>
  <h4>Required Headers</h4>
  <ul>
    <li><strong>Ocp-Apim-Subscription-Key</strong>: The Azure API Management subscription key for your client application (required)</li>
  </ul>
  <h4>Request Format</h4>
  <p>The request body should be a JSON array of sensor readings with the following structure:</p>

  <div class="code-block">
    <pre><code>{{sampleData}}</code></pre>
  </div>

  <h4>Fields</h4>
  <ul>
    <li><strong>deviceId</strong>: The ID of the device sending data (required)</li>
    <li><strong>sensorName</strong>: Name of the sensor measurement (required)</li>
    <li><strong>sensorValue</strong>: The sensor reading value (required)</li>
    <li><strong>timestamp</strong>: ISO 8601 formatted timestamp (e.g. "2024-01-20T15:30:00Z") (required)</li>
  </ul>

  <h4>Device Management</h4>
  <p>Before sending data, devices must be created and associated with your company in the system. 
     You can manage your devices at <a href="/devices" target="_blank">Device Management</a>.</p>
  <p>When creating a new device, only the following fields are required:</p>
  <ul>
    <li><strong>Id</strong>: The device identifier that will be used in API calls</li>
    <li><strong>Name</strong>: A display name for the device</li>
    <li><strong>Category</strong>: Can be set to "Ultralite"</li>
  </ul>
  <p class="p-warning"><i class="pi pi-info-circle"></i> Data sent for unknown devices will be rejected with a 400 Bad Request response.</p>

  <h4>Authentication</h4>
  <p>Include your API key as a query parameter in the URL. You can find your API key on the company details page.</p>

  <h4>Response Codes</h4>
  <ul>
    <li><strong>200 OK</strong>: Data successfully ingested</li>
    <li><strong>400 Bad Request</strong>: Invalid request format or missing required fields</li>
    <li><strong>401 Unauthorized</strong>: Invalid API key</li>
  </ul>

  <h3>Raw Job Data Query</h3>
  <p>To query raw job data for a company, make a GET request to the following endpoint:</p>

  <div class="code-block">
    <code>GET https://integrationflex-apim.azure-api.net/opsviewer/QueryJobData?jobNumber=YOUR_JOB_NUMBER&amp;startTime=2026-03-01T00:00:00Z&amp;endTime=2026-03-01T01:00:00Z</code>
  </div>

  <h4>Required Headers</h4>
  <ul>
    <li><strong>Ocp-Apim-Subscription-Key</strong>: The Azure API Management subscription key for your client application (required)</li>
    <li><strong>X-Company-Api-Key</strong>: The company API key from the company details page (required)</li>
  </ul>

  <h4>Query Parameters</h4>
  <ul>
    <li><strong>jobNumber</strong>: Exact job number match after trimming and uppercasing (required)</li>
    <li>Vendor-company callers match vendor job <strong>Name</strong>; consumer-company callers match consumer job <strong>Number</strong></li>
    <li><strong>startTime</strong>: Start of the UTC time range in ISO 8601 format (required)</li>
    <li><strong>endTime</strong>: End of the UTC time range in ISO 8601 format (required, maximum one hour after startTime)</li>
  </ul>
  <h4>Response Format</h4>
  <p>The response body is raw Influx annotated CSV streamed directly from InfluxDB.</p>
  <p>Influx annotated CSV format reference:
    <a href="https://docs.influxdata.com/influxdb/v2/reference/syntax/annotated-csv/" target="_blank" rel="noopener noreferrer">
      https://docs.influxdata.com/influxdb/v2/reference/syntax/annotated-csv/
    </a>
  </p>

  <h4>Response Codes</h4>
  <ul>
    <li><strong>200 OK</strong>: Raw CSV data returned</li>
    <li><strong>400 Bad Request</strong>: Missing headers or parameters, invalid timestamps, startTime greater than or equal to endTime, or a query range longer than one hour</li>
    <li><strong>401 Unauthorized</strong>: Invalid company API key</li>
    <li><strong>404 Not Found</strong>: No job matched the supplied jobNumber for the company</li>
    <li><strong>409 Conflict</strong>: Multiple jobs matched the supplied jobNumber for the company</li>
    <li><strong>429 Too Many Requests</strong>: Rate limit exceeded for the company key and job number</li>
  </ul>
</div>
